export const ANALYTICS_DEVICES = ["all", "mobile", "tablet", "other"] as const;

export type AnalyticsDevice = (typeof ANALYTICS_DEVICES)[number];

export const queryKeys = {
  all: [{ scope: "handyout-app" }] as const,

  profile: {
    all: [{ scope: "handyout-app", entity: "profile" }] as const,
    me: () => [{ scope: "handyout-app", entity: "profile" }, "me"] as const,
    meWithEmail: () =>
      [
        { scope: "handyout-app", entity: "profile" },
        "me",
        "with-email",
      ] as const,
    billing: () =>
      [{ scope: "handyout-app", entity: "profile" }, "billing"] as const,
  },

  payments: {
    all: [{ scope: "handyout-app", entity: "payments" }] as const,
    list: () =>
      [{ scope: "handyout-app", entity: "payments" }, "list"] as const,
    account: () =>
      [{ scope: "handyout-app", entity: "payments" }, "account"] as const,
  },

  activity: {
    all: [{ scope: "handyout-app", entity: "activity" }] as const,
    events: (limit: number) =>
      [
        { scope: "handyout-app", entity: "activity" },
        "events",
        { limit },
      ] as const,
  },

  presentations: {
    all: [{ scope: "handyout-app", entity: "presentations" }] as const,
    list: () =>
      [{ scope: "handyout-app", entity: "presentations" }, "list"] as const,
    detail: (id: string) =>
      [
        { scope: "handyout-app", entity: "presentations" },
        "detail",
        id,
      ] as const,
    latest: () =>
      [{ scope: "handyout-app", entity: "presentations" }, "latest"] as const,
  },

  handyouts: {
    all: [{ scope: "handyout-app", entity: "handyouts" }] as const,
    list: () =>
      [{ scope: "handyout-app", entity: "handyouts" }, "list"] as const,
    byPresentation: (presId: string) =>
      [
        { scope: "handyout-app", entity: "handyouts" },
        "byPresentation",
        presId,
      ] as const,
    full: (presId: string) =>
      [{ scope: "handyout-app", entity: "handyouts" }, "full", presId] as const,
    intro: (presId: string) =>
      [
        { scope: "handyout-app", entity: "handyouts" },
        "intro",
        presId,
      ] as const,
    detail: (id: string) =>
      [{ scope: "handyout-app", entity: "handyouts" }, "detail", id] as const,
    published: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "handyouts" },
        "published",
        handyoutId,
      ] as const,
    translations: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "handyouts" },
        "translations",
        handyoutId,
      ] as const,
    translationPreviews: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "handyouts" },
        "translation-preview",
        handyoutId,
      ] as const,
    translationPreview: (handyoutId: string, code: string) =>
      [
        { scope: "handyout-app", entity: "handyouts" },
        "translation-preview",
        handyoutId,
        code,
      ] as const,
  },

  referrals: {
    all: [{ scope: "handyout-app", entity: "referrals" }] as const,
    stats: () =>
      [{ scope: "handyout-app", entity: "referrals" }, "stats"] as const,
    joined: () =>
      [{ scope: "handyout-app", entity: "referrals" }, "joined"] as const,
    invited: () =>
      [{ scope: "handyout-app", entity: "referrals" }, "invited"] as const,
    link: () =>
      [{ scope: "handyout-app", entity: "referrals" }, "link"] as const,
    pending: () =>
      [{ scope: "handyout-app", entity: "referrals" }, "pending"] as const,
  },

  sessions: {
    all: [{ scope: "handyout-app", entity: "sessions" }] as const,
    list: () =>
      [{ scope: "handyout-app", entity: "sessions" }, "list"] as const,
    detail: () =>
      [{ scope: "handyout-app", entity: "sessions" }, "detail"] as const,
  },

  analytics: {
    all: [{ scope: "handyout-app", entity: "analytics" }] as const,
    userOverview: (days: number) =>
      [
        { scope: "handyout-app", entity: "analytics" },
        "user",
        "overview",
        days,
      ] as const,
    handyout: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "analytics" },
        "handyout",
        handyoutId,
      ] as const,
    handyoutMetric: (
      handyoutId: string,
      metric: string,
      params?: Record<string, unknown>,
      device?: AnalyticsDevice,
    ) =>
      device && device !== "all"
        ? ([
            { scope: "handyout-app", entity: "analytics" },
            "handyout",
            handyoutId,
            metric,
            params,
            { device },
          ] as const)
        : ([
            { scope: "handyout-app", entity: "analytics" },
            "handyout",
            handyoutId,
            metric,
            params,
          ] as const),
    perSlideEngagement: (handyoutId: string, device?: AnalyticsDevice) =>
      [
        { scope: "handyout-app", entity: "analytics" },
        "handyout",
        handyoutId,
        "per-slide-engagement-points",
        { device: device ?? "all" },
      ] as const,
    topHandyouts: (limit?: number) =>
      limit === undefined
        ? ([
            { scope: "handyout-app", entity: "analytics" },
            "topHandyouts",
          ] as const)
        : ([
            { scope: "handyout-app", entity: "analytics" },
            "topHandyouts",
            { limit },
          ] as const),
  },

  dashboard: {
    all: [{ scope: "handyout-app", entity: "dashboard" }] as const,
    attention: () =>
      [{ scope: "handyout-app", entity: "dashboard" }, "attention"] as const,
    recentComments: (limit: number) =>
      [
        { scope: "handyout-app", entity: "dashboard" },
        "recentComments",
        { limit },
      ] as const,
  },

  qr: {
    all: [{ scope: "handyout-app", entity: "qr" }] as const,
    byHandyout: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "qr" },
        "byHandyout",
        handyoutId,
      ] as const,
    logo: (src: string) =>
      [{ scope: "handyout-app", entity: "qr" }, "logo", src] as const,
  },

  slides: {
    all: [{ scope: "handyout-app", entity: "slides" }] as const,
    byHandyout: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "slides" },
        "byHandyout",
        handyoutId,
      ] as const,
  },

  comments: {
    all: [{ scope: "handyout-app", entity: "comments" }] as const,
    bySlide: (slideId: string) =>
      [
        { scope: "handyout-app", entity: "comments" },
        "bySlide",
        slideId,
      ] as const,
    recent: (handyoutId: string, limit: number) =>
      [
        { scope: "handyout-app", entity: "comments" },
        "recent",
        handyoutId,
        limit,
      ] as const,
    likes: (slideId: string) =>
      [
        { scope: "handyout-app", entity: "comments" },
        "likes",
        slideId,
      ] as const,
    moderation: (slideId: string) =>
      [
        { scope: "handyout-app", entity: "comments" },
        "moderation",
        slideId,
      ] as const,
    summaries: [
      { scope: "handyout-app", entity: "comments" },
      "summary",
    ] as const,
    summary: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "comments" },
        "summary",
        handyoutId,
      ] as const,
    add: (slideId: string) =>
      [
        { scope: "handyout-app", entity: "comments" },
        "add",
        slideId,
      ] as const,
  },

  reactions: {
    all: [{ scope: "handyout-app", entity: "reactions" }] as const,
    bySlide: (slideId: string) =>
      [
        { scope: "handyout-app", entity: "reactions" },
        "bySlide",
        slideId,
      ] as const,
    countsBySlide: (slideId: string) =>
      [
        { scope: "handyout-app", entity: "reactions" },
        "countsBySlide",
        slideId,
      ] as const,
    set: (slideId: string) =>
      [
        { scope: "handyout-app", entity: "reactions" },
        "set",
        slideId,
      ] as const,
  },

  mailing: {
    all: [{ scope: "handyout-app", entity: "mailing" }] as const,
    consentText: (type: string) =>
      [
        { scope: "handyout-app", entity: "mailing" },
        "consent-text",
        type,
      ] as const,
  },

  storage: {
    all: [{ scope: "handyout-app", entity: "storage" }] as const,
    thumbnail: (presentationId: string) =>
      [
        { scope: "handyout-app", entity: "storage" },
        "thumbnail",
        presentationId,
      ] as const,
    slides: (presentationId: string) =>
      [
        { scope: "handyout-app", entity: "storage" },
        "slides",
        presentationId,
      ] as const,
  },

  tourProgress: {
    all: [{ scope: "handyout-app", entity: "tourProgress" }] as const,
    me: () =>
      [{ scope: "handyout-app", entity: "tourProgress" }, "me"] as const,
  },

  stripe: {
    all: [{ scope: "handyout-app", entity: "stripe" }] as const,
    prices: () =>
      [{ scope: "handyout-app", entity: "stripe" }, "prices"] as const,
  },

  knowledge: {
    all: [{ scope: "handyout-app", entity: "knowledge" }] as const,
    articles: () =>
      [{ scope: "handyout-app", entity: "knowledge" }, "articles"] as const,
  },

  moduleMessages: {
    all: [{ scope: "handyout-app", entity: "moduleMessages" }] as const,
    history: (moduleId: string) =>
      [
        { scope: "handyout-app", entity: "moduleMessages" },
        "history",
        moduleId,
      ] as const,
  },

  moduleProposals: {
    all: [{ scope: "handyout-app", entity: "moduleProposals" }] as const,
    byHandyout: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "moduleProposals" },
        "byHandyout",
        handyoutId,
      ] as const,
  },

  moduleRevisions: {
    all: [{ scope: "handyout-app", entity: "moduleRevisions" }] as const,
    byHandyout: (handyoutId: string) =>
      [
        { scope: "handyout-app", entity: "moduleRevisions" },
        "byHandyout",
        handyoutId,
      ] as const,
  },
};
